import React, { useState } from "react";
import { CalendarCheck2, Download, X, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const W = 1080;

const clip = (ctx, s, max) => {
  if (ctx.measureText(s).width <= max) return s;
  while (s.length > 1 && ctx.measureText(s + "…").width > max) s = s.slice(0, -1);
  return s + "…";
};

const drawYearCard = async ({ rounds, score, chartName }) => {
  await Promise.all([document.fonts.load("700 80px Inter"), document.fonts.load("600 40px Inter")]).catch(() => {});
  const rowH = 250;
  const H = 520 + rounds.length * rowH + 200;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#2D4A3E"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "600 30px Inter";
  ctx.letterSpacing = "8px"; ctx.fillText("CURIOVAULT  ·  NAME THE YEAR", 80, 130); ctx.letterSpacing = "0px";
  ctx.fillStyle = "#FFFFFF"; ctx.font = "700 76px Inter, sans-serif";
  ctx.fillText("Year Snapshot", 80, 240);
  ctx.fillStyle = "rgba(255,255,255,0.75)"; ctx.font = "500 34px Inter";
  ctx.fillText(chartName, 80, 300);
  ctx.fillStyle = "#FFFFFF"; ctx.font = "700 150px Inter"; ctx.textAlign = "right";
  ctx.fillText(`${score}/${rounds.length}`, W - 80, 300); ctx.textAlign = "left";
  rounds.forEach((r, i) => {
    const y = 400 + i * rowH;
    ctx.fillStyle = "#3E5D50"; ctx.beginPath(); ctx.roundRect(80, y, W - 160, rowH - 30, 32); ctx.fill();
    ctx.fillStyle = r.ok ? "#E5EEE8" : "#F5E4DF"; ctx.beginPath(); ctx.roundRect(W - 340, y + 30, 230, 70, 35); ctx.fill();
    ctx.fillStyle = r.ok ? "#2D4A3E" : "#9E4535"; ctx.font = "700 36px Inter"; ctx.textAlign = "center";
    ctx.fillText(r.ok ? `✓ ${r.correct}` : `${r.guess || "–"} → ${r.correct}`, W - 225, y + 78); ctx.textAlign = "left";
    ctx.font = "500 30px Inter";
    r.items.slice(0, 3).forEach((it, k) => {
      ctx.fillStyle = k === 0 ? "#FFFFFF" : "rgba(255,255,255,0.82)";
      ctx.fillText(clip(ctx, `${it.title} · ${it.artist}`, k === 0 ? W - 500 : W - 220), 120, y + 76 + k * 52);
    });
  });
  ctx.fillStyle = "#FFFFFF"; ctx.font = "600 46px Inter";
  ctx.fillText(score === rounds.length ? "Every year spot on. Beat that." : "Can you date them better?", 80, H - 120);
  ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "500 34px Inter";
  ctx.fillText("Play free at curiovault.co/trivia", 80, H - 60);
  return new Promise((res) => c.toBlob(res, "image/png"));
};

export const ShareYearCard = ({ rounds, chartName }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const score = rounds.filter((r) => r.ok).length;
  const text = `CurioVault · Name the Year (${chartName})\n${score}/${rounds.length} ${rounds.map((r) => (r.ok ? "🟩" : "🟥")).join("")}\n${rounds.map((r) => `${r.items[0]?.title || ""} → ${r.correct}${r.ok ? " ✓" : ` (I said ${r.guess || "–"})`}`).join("\n")}\nPlay free: https://curiovault.co/trivia`;
  const Pill = "inline-flex items-center justify-center gap-2 font-ui font-semibold text-sm rounded-full px-5 py-2.5 transition-[transform,background-color] active:scale-[0.98]";

  const open = async () => {
    setBusy(true);
    try {
      const blob = await drawYearCard({ rounds, score, chartName });
      setPreview({ url: URL.createObjectURL(blob), blob });
    } finally { setBusy(false); }
  };
  const nativeShare = async () => {
    const file = new File([preview.blob], "curiovault-year-snapshot.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], text, title: "CurioVault Year Snapshot" }); } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(text).then(() => toast.success("Copied, paste it anywhere"));
    }
  };
  const close = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };

  return (
    <>
      <button data-testid="share-year-snapshot-btn" onClick={open} disabled={busy} className={`${Pill} bg-[#2D4A3E] text-white hover:bg-[#243D33] disabled:opacity-60`}>
        <CalendarCheck2 className="w-4 h-4" /> {busy ? "Preparing…" : "Share Year Snapshot"}
      </button>
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid="year-snapshot-modal">
          <div className="bg-[#F4F1EA] rounded-[20px] p-4 sm:p-5 w-full max-w-sm max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-ui font-semibold text-sm text-[#161514]">Your Year Snapshot</p>
              <button onClick={close} data-testid="year-snapshot-close" className="text-[#77716A] hover:text-[#161514]"><X className="w-4 h-4" /></button>
            </div>
            <img src={preview.url} alt="Name the Year snapshot card" data-testid="year-snapshot-image" className="w-full rounded-[14px] border border-[#DDD5C7]" />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <a href={preview.url} download="curiovault-year-snapshot.png" data-testid="year-snapshot-download" className={`${Pill} bg-[#161514] text-white hover:bg-black`}><Download className="w-4 h-4" /> Download</a>
              <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" data-testid="year-snapshot-whatsapp" className={`${Pill} bg-[#2D4A3E] text-white hover:bg-[#243D33]`}><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
            <button data-testid="year-snapshot-share" onClick={nativeShare} className={`${Pill} w-full mt-2 bg-white border border-[#DDD5C7] text-[#161514] hover:bg-[#F7F4EE]`}><Copy className="w-4 h-4" /> Share or copy text</button>
          </div>
        </div>
      )}
    </>
  );
};
